import React, { useEffect } from 'react';
import { View, Text, Pressable, ActivityIndicator, Platform } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import AsyncStorage from '@react-native-async-storage/async-storage';
import SearchBox from '@/components/General/inputs/SearchBox';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import { Tooltip } from '@/components/General/Tooltip';
import DropdownMenu from '@/components/General/DropdownMenu';
import PeopleAdvancedFilter from '@/components/Filters/PeopleAdvancedFilter';
import Toast from 'react-native-toast-message';
import usePlatform from '@/utils/hooks/usePlatform';

export default function CheckinToolbar({
    kioskModeEnabled,
    searchText,
    setSearchText,
    attendance,
    checkinLocation,
    eventInstance,
    selectedFilter,
    setSelectedFilter,
    setCheckinLocation,
    viewType,
    setViewType,
    labelQueue,
    realPrinters,
    availablePrinters,
    printers,
    devTestPrinter,
    setDevTestPrinter,
    isPrintingAvailable,
    onCheckInAndPrint,
    onCheckInOnly,
    onPrintOnly,
    onPreviewLabels,
    onClearPending,
    onOpenAnonymousHeadCountModal,
    isConnected,
    realtimeEnabled,
    onToggleRealtime,
    isFetching,
    // Native-only props for Brother printer:
    selectedPrinter,
    onOpenPrinterSheet,
}) {
    const { isLandscape } = usePlatform();
    const { theme: { colors } } = useUnistyles();

    const CHECKIN_LOCATION_KEY = eventInstance?.eventID ? `@checkin_location_${eventInstance.eventID}` : null;

    const isAllLocations = !checkinLocation;

    useEffect(() => {
        if (!CHECKIN_LOCATION_KEY || !eventInstance?.Locations?.length) return;

        const loadSavedLocation = async () => {
            try {
                const savedLocationId = await AsyncStorage.getItem(CHECKIN_LOCATION_KEY);
                if (savedLocationId === 'ALL') {
                    if (checkinLocation !== null) {
                        setCheckinLocation(null);
                        setSelectedFilter("Everyone");
                    }
                } else if (savedLocationId) {
                    const savedLocation = eventInstance.Locations.find(l => l.id === savedLocationId);
                    if (savedLocation && savedLocation.id !== checkinLocation?.id) {
                        setCheckinLocation(savedLocation);
                        setSelectedFilter(savedLocation?.CheckInFilter ? "Suggested" : "Everyone");
                    }
                }
            } catch {
                // Silently ignore storage errors
            }
        };

        loadSavedLocation();
    }, [CHECKIN_LOCATION_KEY, eventInstance?.Locations, eventInstance?.eventID]);

    const filters = isAllLocations
        ? ["Everyone", "Recent", "Checked In", "Not Checked In", ...(isPrintingAvailable ? ["Awaiting Print"] : [])]
        : checkinLocation?.CheckInFilter
            ? ["Everyone", "Suggested", "Recent", "Checked In", "Not Checked In", ...(isPrintingAvailable ? ["Awaiting Print"] : [])]
            : ["Everyone", "Recent", "Checked In", "Not Checked In", ...(isPrintingAvailable ? ["Awaiting Print"] : [])];

    // Compute the filter list for a (possibly not-yet-applied) location so we can
    // keep the user's current main filter when switching locations — only reset
    // when the current filter is no longer valid for the new location.
    const getFiltersForLocation = (location, printingAvailable) => {
        if (!location) {
            return ["Everyone", "Recent", "Checked In", "Not Checked In", ...(printingAvailable ? ["Awaiting Print"] : [])];
        }
        return location?.CheckInFilter
            ? ["Everyone", "Suggested", "Recent", "Checked In", "Not Checked In", ...(printingAvailable ? ["Awaiting Print"] : [])]
            : ["Everyone", "Recent", "Checked In", "Not Checked In", ...(printingAvailable ? ["Awaiting Print"] : [])];
    };

    const isAwaitingPrintMode = selectedFilter === "Awaiting Print";

    const attendanceCountsByLocation = attendance?.reduce((acc, attendee) => {
        const locationName = attendee.Location?.name;
        if (!acc[locationName]) {
            acc[locationName] = { count: 0, checkedOutCount: 0 };
        }
        acc[locationName].count++;
        if (attendee.checkedOutAt) {
            acc[locationName].checkedOutCount++;
        }
        return acc;
    }, {});

    const currentLocationName = checkinLocation?.name;
    const totalCount = attendance?.length ?? 0;
    const totalCheckedOutCount = attendance?.filter(a => a.checkedOutAt)?.length ?? 0;
    const currentLocationCount = isAllLocations ? totalCount : (attendanceCountsByLocation?.[currentLocationName]?.count ?? 0);
    const currentLocationCheckedOutCount = isAllLocations ? totalCheckedOutCount : (attendanceCountsByLocation?.[currentLocationName]?.checkedOutCount ?? 0);

    const hasLocations = eventInstance?.Locations?.length > 0;
    const locationBreakdownLines = hasLocations ? Object.entries(attendanceCountsByLocation || {}).map(([locationName, location]) => `${locationName}: ${location.count}`) : [];
    const checkedOutBreakdownLines = hasLocations ? Object.entries(attendanceCountsByLocation || {}).map(([locationName, location]) => `${locationName}: ${location.checkedOutCount}`) : [];

    return (
        <View style={styles.toolbar(kioskModeEnabled)}>
            <View style={styles.toolbarRow(kioskModeEnabled)}>
                <View style={styles.searchWrapper(kioskModeEnabled)}>
                    <SearchBox testID="CheckInSearchBox" searchText={searchText} onChangeText={(e) => setSearchText(e.toLowerCase())} />
                </View>

                <View style={styles.statsContainer}>
                    <Tooltip
                        title={realtimeEnabled ? (isConnected ? "Realtime connection active - tap to disable" : "Realtime connection lost - tap to disable") : "Realtime connection disabled - tap to refresh and re-enable"}
                        style={styles.statBadge}
                        onPress={onToggleRealtime}
                    >
                        <Unicon name={realtimeEnabled && isConnected ? "wifi" : "wifi-off"} library='feather' size={16} colorVariant={realtimeEnabled && isConnected ? "success" : "error"} />
                    </Tooltip>

                    <Tooltip
                        title="Number of attendees who have been checked in"
                        subtitle={[...locationBreakdownLines, `Total: ${totalCount}`].join('\n')}
                        style={styles.statBadge}
                        onPress={onOpenAnonymousHeadCountModal}
                    >
                        <Unicon name="checkmark-circle" size={16} colorVariant="success" />
                        <Text style={styles.statText}>{currentLocationCount}</Text>
                    </Tooltip>

                    <Tooltip
                        title="Number of attendees who have been checked out after attending"
                        subtitle={[...checkedOutBreakdownLines, `Total: ${totalCheckedOutCount}`].join('\n')}
                        style={styles.statBadge}
                        onPress={onOpenAnonymousHeadCountModal}
                    >
                        <Unicon name="exit" size={16} colorVariant="info" />
                        <Text style={styles.statText}>{currentLocationCheckedOutCount}</Text>
                    </Tooltip>

                    {(isPrintingAvailable && (isLandscape || Platform.OS !== 'web')) &&
                        <Tooltip
                            title={selectedPrinter?.isConnected ? selectedPrinter.modelName : (availablePrinters?.length > 0 ? (devTestPrinter ? 'Dev Test Printer' : printers[0]?.name) : "No printer connected — tap to select")}
                            style={styles.statBadge}
                            onPress={onOpenPrinterSheet || (__DEV__ ? () => {
                                setDevTestPrinter(prev => !prev);
                                if (!devTestPrinter) Toast.show({ type: 'info', text2: 'Dev test printer enabled' });
                                else { onClearPending(); Toast.show({ type: 'info', text2: 'Dev test printer disabled' }); }
                            } : undefined)}
                        >
                            <Unicon name="print" size={16} colorVariant={selectedPrinter?.isConnected || availablePrinters?.length > 0 ? (devTestPrinter ? "warning" : "success") : "error"} />
                        </Tooltip>
                    }
                </View>
            </View>

            {labelQueue.length > 0 &&
                <View style={styles.labelQueueBar}>
                    <View style={styles.labelQueueBarLeft}>
                        <View style={styles.labelQueueBadge}>
                            <Text style={styles.labelQueueBadgeText}>{labelQueue.length}</Text>
                        </View>
                        <Text style={styles.labelQueueBarTitle}>{labelQueue.length === 1 ? "Person" : "People"} Selected</Text>
                    </View>
                    <View style={styles.labelQueueBarActions}>
                        {isAwaitingPrintMode ? (
                            <>
                                <SolidButton iconName="print" size="sm" text="Print" disabled={realPrinters?.length === 0} onPress={onPrintOnly} type="primary" />
                                <SolidButton iconName="eye" outline size="sm" text="Preview" onPress={onPreviewLabels} type="primary" />
                            </>
                        ) : (
                            <>
                                <SolidButton iconName="print" size="sm" text="Check In & Print" disabled={realPrinters?.length === 0} onPress={onCheckInAndPrint} type="primary" />
                                <SolidButton iconName="person-add" outline size="sm" text="Check In Only" onPress={onCheckInOnly} type="primary" />
                                <SolidButton iconName="eye" outline size="sm" text="Preview" onPress={onPreviewLabels} type="primary" />
                            </>
                        )}
                        <Pressable onPress={onClearPending} style={styles.labelQueueClearButton}>
                            <Unicon name="close" size={16} colorVariant="textSecondary" />
                        </Pressable>
                    </View>
                </View>
            }

            {!kioskModeEnabled &&
                <View style={styles.filtersRow}>
                    <View style={styles.filterChipsContainer}>
                        {selectedFilter === "Everyone" && <PeopleAdvancedFilter filterId="checkin" />}

                        <DropdownMenu
                            title="Filter"
                            triggerComponent={
                                <View style={styles.locationSelector}>
                                    <Unicon name="funnel" size={14} colorVariant="primary" />
                                    <Text style={styles.locationText}>{selectedFilter}</Text>
                                    <Unicon name="chevron-down" size={14} colorVariant="textSecondary" />
                                </View>
                            }
                            options={filters.map(filter => ({
                                name: filter,
                                onSelect: () => setSelectedFilter(filter),
                            }))}
                        />

                        {eventInstance?.Locations?.length > 1 &&
                            <DropdownMenu
                                title="Location"
                                triggerComponent={
                                    <View style={styles.locationSelector}>
                                        <Unicon name="location" size={14} colorVariant="primary" />
                                        <Text style={styles.locationText}>{isAllLocations ? "All Locations" : (checkinLocation?.name || eventInstance?.Locations?.[0]?.name)}</Text>
                                        <Unicon name="chevron-down" size={14} colorVariant="textSecondary" />
                                    </View>
                                }
                                options={[
                                    {
                                        name: "All Locations",
                                        onSelect: async () => {
                                            onClearPending();
                                            setCheckinLocation(null);
                                            const newFilters = getFiltersForLocation(null, isPrintingAvailable);
                                            if (!newFilters.includes(selectedFilter)) setSelectedFilter("Everyone");
                                            if (CHECKIN_LOCATION_KEY) {
                                                try {
                                                    await AsyncStorage.setItem(CHECKIN_LOCATION_KEY, 'ALL');
                                                } catch {
                                                    // Silently ignore storage errors
                                                }
                                            }
                                        },
                                    },
                                    ...(eventInstance?.Locations?.map(l => ({
                                        name: l.name,
                                        onSelect: async () => {
                                            onClearPending();
                                            setCheckinLocation(l);
                                            const newFilters = getFiltersForLocation(l, isPrintingAvailable);
                                            if (!newFilters.includes(selectedFilter)) setSelectedFilter(l?.CheckInFilter ? "Suggested" : "Everyone");
                                            if (CHECKIN_LOCATION_KEY) {
                                                try {
                                                    await AsyncStorage.setItem(CHECKIN_LOCATION_KEY, l.id);
                                                } catch {
                                                    // Silently ignore storage errors
                                                }
                                            }
                                        },
                                    })) || []),
                                ]}
                            />
                        }

                        {isFetching && <ActivityIndicator size="small" color={colors.primary} />}
                    </View>

                    <View style={{ flex: 1 }} />

                        {isLandscape && (
                            <View style={styles.viewToggle}>
                                <Pressable onPress={() => setViewType("cards")} style={styles.viewToggleButton(viewType === "cards")}>
                                    <Unicon name="grid" size={16} colorVariant={viewType === "cards" ? "primary" : "textSecondary"} />
                                </Pressable>
                                <Pressable onPress={() => setViewType("table")} style={styles.viewToggleButton(viewType === "table")}>
                                    <Unicon name="list" size={16} colorVariant={viewType === "table" ? "primary" : "textSecondary"} />
                                </Pressable>
                            </View>
                        )}
                    </View> 
            }
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    toolbar: (kioskMode) => ({
        paddingHorizontal: 16,
        paddingTop: kioskMode ? 16 : 12,
        paddingBottom: 12,
        backgroundColor: colors.background,
        width: "100%",
        alignSelf: "center",
    }),
    toolbarRow: (kioskMode) => ({
        flexDirection: "row",
        alignItems: "center",
        justifyContent: kioskMode ? "center" : "flex-start",
        gap: 12,
    }),
    searchWrapper: (kioskMode) => ({
        flex: kioskMode ? undefined : 1,
        width: kioskMode ? 400 : undefined,
        maxWidth: 500,
    }),
    statsContainer: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    statBadge: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 20,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        _web: { cursor: "pointer", _hover: { opacity: 0.8 } },
    },
    statText: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    filtersRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 12,
        flexWrap: "wrap",
        gap: 8,
    },
    filterChipsContainer: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
    },
    filterChip: (isActive) => ({
        paddingVertical: 6,
        paddingHorizontal: 12,
        borderRadius: 16,
        backgroundColor: isActive ? colors.primary : colors.surface,
        boxShadow: shadows.card,
        _web: { cursor: "pointer", _hover: { opacity: 0.85 } },
    }),
    filterChipText: (isActive) => ({
        fontSize: 13,
        fontFamily: fonts.heading,
        color: isActive ? colors.onPrimary : colors.textSecondary,
    }),
    locationSelector: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 6,
        paddingHorizontal: 12,
        borderRadius: 8,
        backgroundColor: colors.surface,
        _web: { cursor: "pointer", _hover: { opacity: 0.85 } },
    },
    locationText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
    },
    viewToggle: {
        flexDirection: "row",
        backgroundColor: colors.surface,
        borderRadius: 8,
        padding: 2,
        marginLeft: 8,
    },
    viewToggleButton: (isActive) => ({
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 6,
        backgroundColor: isActive ? colors.background : "transparent",
        _web: { cursor: "pointer", _hover: { opacity: 0.85 } },
    }),
    labelQueueBar: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: colors.warningSubtle,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        marginTop: 8,
        gap: 12,
    },
    labelQueueBarLeft: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
    },
    labelQueueBadge: {
        backgroundColor: colors.warning,
        borderRadius: 10,
        minWidth: 22,
        height: 22,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 6,
    },
    labelQueueBadgeText: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.onWarning,
    },
    labelQueueBarTitle: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    labelQueueBarActions: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
        justifyContent: "flex-end",
        flex: 1,
    },
    labelQueueClearButton: {
        width: 28,
        height: 28,
        borderRadius: 6,
        backgroundColor: colors.background,
        alignItems: "center",
        justifyContent: "center",
    },
}));
